import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import AdminLayout from "../layout/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { AdminTransaction, AdminUpdateWalletStatus, AdminUserTransactions } from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import PaginationBar from "../ui/PaginationBar";
import { DEFAULT_PAGE_SIZE } from "../../lib/pagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const AdminTransactions = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { userId } = useParams();



  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedTx, setSelectedTx] = useState(null);
  const [editTx, setEditTx] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const effectiveUserId = userId || "";

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, statusFilter, debouncedSearch, dateFrom, dateTo, pageSize]);

  const serverParams = useMemo(
    () => ({
      page: currentPage,
      page_size: pageSize,
      ...(typeFilter !== "all" && { transaction_type: typeFilter }),
      ...(statusFilter !== "all" && { status: statusFilter }),
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(dateFrom && { date_from: new Date(dateFrom).toISOString() }),
      ...(dateTo && { date_to: new Date(dateTo).toISOString() }),
    }),
    [currentPage, pageSize, typeFilter, statusFilter, debouncedSearch, dateFrom, dateTo]
  );

  const { data: txResponse, isLoading, isFetching, error } = useQuery({
    queryKey: ["adminTransactions", effectiveUserId, serverParams],
    queryFn: () => effectiveUserId
        ? AdminUserTransactions(effectiveUserId, serverParams)
        : AdminTransaction(serverParams),
    placeholderData: keepPreviousData,
  });

  const allTransactions = txResponse?.results || [];
  const totalCount = txResponse?.count || 0;

  const filteredTransactions = useMemo(
    () =>
      allTransactions.map((tx, index) => ({
        ...tx,
        index: (currentPage - 1) * pageSize + index + 1,
        amountNum: parseFloat(tx.amount || 0),
        createdDate: new Date(tx.created_at).toLocaleDateString(),
        createdTime: new Date(tx.created_at).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      })),
    [allTransactions, currentPage, pageSize]
  );

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => AdminUpdateWalletStatus(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminTransactions"] });
      setEditTx(null);
      setNewStatus("");
      alert("Transaction status updated successfully");
    },
    onError: (err) => {
      alert("Failed to update status: " + (err.message || "Unknown error"));
    },
  });

  const handleSaveStatus = () => {
    if (!newStatus || newStatus === editTx?.status) {
      setEditTx(null);
      return;
    }
    updateStatusMutation.mutate({ id: editTx.id, status: newStatus });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setTypeFilter("all");
    setStatusFilter("all");
    setDateFrom("");
    setDateTo("");
  };

  const handlePageChange = (page) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentPage(page);
  };

  if (user.role !== "ADMIN") {
    return (
      <div className="flex items-center justify-center min-h-screen text-xl text-red-600">
        Access Denied
      </div>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {effectiveUserId ? "User Transactions" : "Wallet Transactions"}
        </h1>
        <p className="text-gray-500 mt-1">
          {effectiveUserId 
            ? `Wallet transactions for User ID: ${effectiveUserId}` 
            : "View and manage all wallet transaction records"}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-5 mb-6 space-y-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            placeholder="Search by name, reference, description, amount..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1"
          />
          <Button variant="outline" onClick={clearFilters} className="shrink-0">
            Clear Filters
          </Button>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">
              Transaction Type
            </label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="debit">Debit</SelectItem>
                <SelectItem value="credit">Credit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Date From</label>
            <input
              type="datetime-local"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Date To</label>
            <input
              type="datetime-local"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-xl">
          Failed to load transactions: {error?.message || "Unknown error"}
        </div>
      ) : isLoading ? (
        <div className="text-center py-20 text-gray-500 text-lg">
          Loading transactions...
        </div>
      ) : filteredTransactions.length === 0 ? (
        <div className="bg-white p-12 rounded-xl shadow-sm text-center text-gray-500 border">
          No transactions match the current filters.
        </div>
      ) : (
        <div
          className={`bg-white rounded-xl shadow-sm overflow-hidden border transition-opacity duration-150 ${
            isFetching ? "opacity-60 pointer-events-none" : "opacity-100"
          }`}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1050px] text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">#</th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">User</th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">Type</th>
                  <th className="text-right py-4 px-5 font-medium text-gray-600">Amount</th>
                  <th className="text-right py-4 px-5 font-medium text-gray-600">Balance Before</th>
                  <th className="text-right py-4 px-5 font-medium text-gray-600">Balance After</th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">Description</th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">Reference</th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">Date & Time</th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">Status</th>
                  <th className="text-left py-4 px-5 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTransactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3.5 px-5 text-gray-400">{tx.index}</td>
                    <td className="py-3.5 px-5 font-medium">{tx.user_name || "—"}</td>
                    <td className="py-3.5 px-5">
                      <span
                        className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                          tx.transaction_type === "debit"
                            ? "bg-red-100 text-red-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {tx.transaction_type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3.5 px-5 font-semibold text-right">
                      ₦{tx.amountNum.toLocaleString()}
                    </td>
                    <td className="py-3.5 px-5 text-right text-gray-500">
                      {tx.balance_before != null ? `₦${parseFloat(tx.balance_before).toLocaleString()}` : "—"}
                    </td>
                    <td className="py-3.5 px-5 text-right text-gray-700 font-medium">
                      {tx.balance_after != null ? `₦${parseFloat(tx.balance_after).toLocaleString()}` : "—"}
                    </td>
                    <td className="py-3.5 px-5 text-gray-600 max-w-[200px] truncate">
                      {tx.description || "—"}
                    </td>
                    <td className="py-3.5 px-5 font-mono text-xs text-gray-500 break-all">
                      {tx.reference}
                    </td>
                    <td className="py-3.5 px-5 text-gray-500 whitespace-nowrap">
                      <div>{tx.createdDate}</div>
                      <div className="text-xs text-gray-400">{tx.createdTime}</div>
                    </td>
                    <td className="py-3.5 px-5">
                      <span
                        className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                          tx.status === "success"
                            ? "bg-green-100 text-green-700"
                            : tx.status === "failed"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {tx.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3.5 px-5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedTx(tx)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PaginationBar
            page={currentPage}
            pageSize={pageSize}
            total={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={setPageSize}
            isFetching={isFetching}
            noun="transactions"
          />
        </div>
      )}

      {/* View Transaction Modal - Unchanged */}
      <Dialog open={!!selectedTx} onOpenChange={() => setSelectedTx(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
          </DialogHeader>
          {selectedTx && (
            <div className="space-y-4 py-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">ID</p>
                  <p className="font-medium">{selectedTx.id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Reference</p>
                  <p className="font-mono text-xs break-all">{selectedTx.reference}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">User</p>
                <p className="font-medium">{selectedTx.user_name || "—"}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Type</p>
                  <span
                    className={`px-2.5 py-1 text-xs rounded-full font-semibold ${
                      selectedTx.transaction_type === "debit"
                        ? "bg-red-100 text-red-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {selectedTx.transaction_type?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Amount</p>
                  <p className="text-xl font-bold">
                    ₦{selectedTx.amountNum.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Balance Before</p>
                  <p className="font-medium">{selectedTx.balance_before != null ? `₦${parseFloat(selectedTx.balance_before).toLocaleString()}` : "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Balance After</p>
                  <p className="font-medium">{selectedTx.balance_after != null ? `₦${parseFloat(selectedTx.balance_after).toLocaleString()}` : "—"}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Description</p>
                <p>{selectedTx.description || "—"}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Status</p>
                  <span
                    className={`px-2.5 py-1 text-xs rounded-full font-semibold ${
                      selectedTx.status === "success"
                        ? "bg-green-100 text-green-700"
                        : selectedTx.status === "failed"
                        ? "bg-red-100 text-red-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {selectedTx.status?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Date & Time</p>
                  <p>
                    {selectedTx.createdDate} • {selectedTx.createdTime}
                  </p>
                </div>
              </div>
              {selectedTx.rrr && (
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">RRR</p>
                  <p className="font-mono">{selectedTx.rrr}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedTx(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Modal - Unchanged */}
      <Dialog open={!!editTx} onOpenChange={() => setEditTx(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Transaction Status</DialogTitle>
          </DialogHeader>
          {editTx && (
            <div className="py-4 space-y-4 text-sm">
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Reference</p>
                <p className="font-mono text-xs">{editTx.reference}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">User</p>
                <p className="font-medium">{editTx.user_name || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">
                  Current Status:{" "}
                  <strong className="uppercase">{editTx.status}</strong>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  New Status
                </label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTx(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveStatus}
              disabled={
                updateStatusMutation.isLoading ||
                !newStatus ||
                newStatus === editTx?.status
              }
            >
              {updateStatusMutation.isLoading ? "Updating..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminTransactions;